<template>
    <v-container>
        <!-- TOOLBAR --------------------------------------------------------------------------- -->
        <v-toolbar flat class="chartsize">
            <v-toolbar-title>
                {{ accountTitle }}
            </v-toolbar-title>

            <v-spacer></v-spacer>

            <v-btn icon @click="showPreviousDate">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <v-btn icon @click="showToday">
                <v-icon>mdi-calendar-today</v-icon>
            </v-btn>

            <v-toolbar-title>
                {{ localDate }}
            </v-toolbar-title>

            <v-btn icon @click="showNextDate">
                <v-icon>mdi-chevron-right</v-icon>
            </v-btn>

            <v-spacer></v-spacer>
        </v-toolbar>

        <!-- CHART  ---------------------------------------------------------------------------- -->
        <div class="chartsize">
            <line-chart :chart-data="datacollection" :options="chartOptions"></line-chart>
        </div>

    </v-container>
</template>

<script>
import { API, Hub } from 'aws-amplify';
import { listSensorDayReadingsByAccountByDate } from '@/graphql/queries';

import LineChart from './LineChart.js';
import 'chartjs-plugin-colorschemes';

import { compareSensorDayReadingByName, stringToColour, pingAccount, peek } from '@/tools';
import { text } from '@/language';

const dayjs = require( 'dayjs' );
const utcPlugin = require( 'dayjs/plugin/utc' );
dayjs.extend( utcPlugin );

const chartMin = -9;
const chartMax = 30;
const chartStep = 3;

peek( 'do not complain' );

export default {
    name: 'GraphView',

    components: {
        LineChart
    },

    async created() {
        this.globalState = this.$root.$data.globalState;
        Hub.listen( 'monitor', this.hubListenCallback );
    },

    mounted() {
        this.showAccount();
    },

    destroyed() {
        Hub.remove( 'monitor', this.hubListenCallback );
    },

    data: () => ({

        globalState : {},
        accountTitle: '',
        datacollection : null,
        chartOptions : {
            title: {
                display : true,
                text : text( 'graphViewTitle' ),
                position : 'bottom',
            },
            scales : {
                xAxes: [ {
                    'type' : 'time',
                    display : true,
                    position : 'top',
                    time: {
                        unit: 'hour',
                        stepSize : 1,
                    },
                    ticks : {
                        // min : dayjs().hour( 0 ),
                        // max : dayjs().hour(23),
                        source : 'labels',
                        major : {
                            fontStyle : 'bold',
                            fontColor : '#FF0000',
                        },
                    },
                } ],
                yAxes : [ {
                    display : true,
                    position : 'left',
                    gridLines : {
                        color : ( () => {
                            const numberOfLines = ( ( chartMax - chartMin ) / chartStep ) | 0;
                            const zeroIndex = ( chartMax / chartStep ) | 0

                            // Put a red high line and a blue low line.
                            // with the step size of 3 this are at 9 and 3 degrees.

                            var colors = Array( numberOfLines ).fill( 'light-gray' );
                            colors[zeroIndex - 3] = 'red';
                            colors[zeroIndex - 1] = 'blue';

                            return colors;
                        } )(),
                        zeroLineWidth : 2,
                    },
                    ticks : {
                        min: chartMin,
                        max: chartMax,
                        stepSize : chartStep,
                        callback : ( value ) => value + '°',
                    },
                    // scaleLabel : {
                    //     display : true,
                    //     labelString : 'Grados Centígrados', // '°C'
                    // },
                } ],
            },
            plugins: {
                annotation : {
                    annotations : [
                        {
                            drawTime : 'afterDraw',
                            id : 'low-line',
                            type : 'line',
                            mode : 'horizontal',
                            scaleID : 'y-axis-0',
                            value : 10.0,
                            borderColor : 'black',
                            borderWidth : 10,
                        },
                    ],
                },
                //     colorschemes: {
                //         //scheme: 'brewer.DarkTwo8',
                //         //scheme: 'brewer.SetOne9',
                //         //scheme: 'brewer.SetTwo8',
                //         //scheme: 'office.Apothecary6',
                //         //scheme: 'office.Forte6',
                //         //scheme: 'brewer.Accent8',
                //         scheme: 'brewer.Paired12'
                //     }
            }
        },
    }),

    computed : {
        localDate() {
            return this.globalState.localDate();
        },
    },

    watch: {
    },

    methods : {

        hubListenCallback( data ) {
            if ( ! ['ev:setAccount', 'ev:changeDate'].includes( data.payload.event ) )
                return;

            this.showAccount();
        },

        showToday() {
            this.globalState.setDate( dayjs() );
        },

        showPreviousDate() {
            this.globalState.changeDate( -1 );
        },

        showNextDate() {
            this.globalState.changeDate( 1 );
        },

        showAccount() {
            const account = this.globalState.account;
            const localDate = this.globalState.localDate();

            if ( account ) 
                this.accountTitle = account.location + ': ' + account.description;
            else 
                this.accountTitle = '';

            this.getSensorDayReadings( account, localDate )
                .then( sensorDayReadings => this.makeGraphData( account, sensorDayReadings ) )
                // .then( peek( 'graphData' ) )
                .then( graphData => this.datacollection = graphData )
            ;
        },

        getAccountDevices( account ) {
            return pingAccount( this.globalState.accountDevicesCache, account );
        },

        async makeGraphData( account, sensorDayReadings ) {
            // let debug = 0;
            let minUnixtime = null;

            const makeSensorDataset = function ( sensorDayReading ) {

                const makeReadingPoint = function ( reading ) {
                    const timeAtSensor = dayjs.unix( reading.unixtime ); 
                    if ( minUnixtime == null || minUnixtime > reading.unixtime )
                        minUnixtime = reading.unixtime;

                    return {
                        x : timeAtSensor.format(),
                        y : reading.reading,
                    };
                };

                const channel1Dataset = {
                    label : sensorDayReading.name,
                    name : sensorDayReading.name, // so that we can sort with compareSensorDayReadingByName
                    fill : false,
                    data : sensorDayReading.readings.map( makeReadingPoint ),
                    pointRadius : 0,
                    borderColor : stringToColour( sensorDayReading.serial + sensorDayReading.name + account.loginId ),
                    borderWidth : 2,
                };
                const hasChannel2 = sensorDayReading.channel2Name != null;
                const channel2Dataset = ! hasChannel2 ? {} : {
                    label : sensorDayReading.channel2Name,
                    name : sensorDayReading.channel2Name, // so that we can sort with compareSensorDayReadingByName
                    fill : false,
                    data : sensorDayReading.channel2Readings.map( makeReadingPoint ),
                    pointRadius : 0,
                    borderColor : stringToColour( sensorDayReading.serial + sensorDayReading.channel2Name + account.loginId ),
                    borderWidth : 2,
                };

                if ( hasChannel2 )
                    return [ channel1Dataset, channel2Dataset ];

                return [ channel1Dataset ];
            };

            const dayReadingsSerials = sensorDayReadings.map( sensorDayReading => sensorDayReading.serial );
            const devices = await this.getAccountDevices( account ).catch( error => {
                console.log( 'Error looking up account devices, will not pad graphic with empty datasets', error );
                return [];
            } );

            const emptyDatasets = devices
                .filter( device => ! dayReadingsSerials.includes( device.serial ) )
                .flatMap( device => {
                    const makeHeader = ( name ) => ({
                        label : name,
                        name : name, // so that we can sort with compareSensorDayReadingByName
                        fill : false,
                        data : [],
                        pointRadius : 0,
                        borderColor : stringToColour( device.serial + name + account.loginId ),
                        borderWidth : 2,
                    } );

                    const hasChannel2 = device.channel.length > 1;
                    const channel1Header = makeHeader( device.name + ( hasChannel2 ? ' ' + device.channel[0].name : '' ) );
                    const channel2Header = ! hasChannel2 ? {} : makeHeader( device.name + ' ' + device.channel[1].name );
                    if ( hasChannel2 )
                        return [ channel1Header, channel2Header ];

                    return [ channel1Header ];
                } );

            // console.log( { devices : devices.filter( device => ! dayReadingsSerials.includes( device.serial ) ), dayReadingsSerials, emptyDatasets } );

            const datasets = sensorDayReadings.flatMap( makeSensorDataset )
                .concat( emptyDatasets )
                .sort( compareSensorDayReadingByName );

            let labels = [...Array( 25 ).keys()].map( hour =>
                dayjs( this.localDate ).hour( hour ).minute( 0 ).format() );
            if ( minUnixtime != null ) {
                labels = [...Array( 25 ).keys()].map( hour =>
                    dayjs.unix( minUnixtime ).add( hour, 'hour' ).minute( 0 ).second( 0 ).format() );
            }

            return { labels, datasets };
        },

        ////////////////////////////////////////////////////////////////////////////////
        // Back end

        async getSensorDayReadings( account, localDate ) {
            if ( ! account ) {
                return Promise.resolve( [] );
            }

            const params = {
                query : listSensorDayReadingsByAccountByDate,
                variables : {
                    tndAccountLoginId : account.loginId,
                    localDate : { eq : localDate },
                    limit : 1000,
                },
            };

            return API.graphql( params )
                      .then( result => result.data.listSensorDayReadingsByAccountByDate.items )
                      .catch( error => {
                          console.log( 'Error calling listSensorDayReadingsByAccountByDate(): ', error );
                          return [];
                      } );
        },


        subscribe() {
        },
    },
}
</script>

<style>
 .chartsize {
     max-width: 700px;
 }
</style>
